import React, { useEffect, useRef, useState } from "react";
import { FaCheck, FaCheckCircle, FaChevronDown, FaClock } from "react-icons/fa";
import "../ConnectedProjectView.css";
import ExpandedProjectView from "./ExpandedProjectView";

const ConnectedProjectView = ({ projects }) => {
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [mobilePickerOpen, setMobilePickerOpen] = useState(false);
  const mobilePickerRef = useRef(null);
  const mobilePickerButtonRef = useRef(null);

  useEffect(() => {
    const selectionStillExists = projects.some(
      (project) => project._id === selectedProjectId
    );
    if (!selectionStillExists) {
      setSelectedProjectId(
        projects.find((project) => project.joinStatus === "approved")?._id ||
          projects[0]?._id ||
          ""
      );
    }
  }, [projects, selectedProjectId]);

  const selectedProject = projects.find(
    (project) => project._id === selectedProjectId
  );

  useEffect(() => {
    if (!mobilePickerOpen) return undefined;

    const closeOnOutsideClick = (event) => {
      if (!mobilePickerRef.current?.contains(event.target)) {
        setMobilePickerOpen(false);
      }
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") {
        setMobilePickerOpen(false);
        mobilePickerButtonRef.current?.focus();
      }
    };

    document.addEventListener("pointerdown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [mobilePickerOpen]);

  const selectProject = (projectId) => {
    setSelectedProjectId(projectId);
    setMobilePickerOpen(false);
    mobilePickerButtonRef.current?.focus();
  };

  return (
    <div className="connected-project-view">
      <aside className="project-switcher" aria-label="My projects">
        <div className="project-switcher__header">
          <div>
            <h2>Your projects</h2>
            <p>{projects.length} programme{projects.length === 1 ? "" : "s"}</p>
          </div>
        </div>

        <div className="project-switcher__mobile-picker" ref={mobilePickerRef}>
          <span className="project-switcher__mobile-label" id="mobile-project-label">
            Selected project
          </span>
          <button
            ref={mobilePickerButtonRef}
            type="button"
            className={`project-switcher__mobile-trigger${mobilePickerOpen ? " is-open" : ""}`}
            aria-labelledby="mobile-project-label mobile-project-value"
            aria-haspopup="listbox"
            aria-expanded={mobilePickerOpen}
            onClick={() => setMobilePickerOpen((open) => !open)}
          >
            <span className="project-switcher__mobile-trigger-copy">
              <span id="mobile-project-value" className="project-switcher__mobile-value">
                {selectedProject?.name || "Choose a project"}
              </span>
              {selectedProject ? (
                <span className="project-switcher__mobile-status">
                  {selectedProject.joinStatus === "approved" ? "Active" : "Awaiting approval"}
                </span>
              ) : null}
            </span>
            <span className="project-switcher__mobile-chevron" aria-hidden="true">
              <FaChevronDown />
            </span>
          </button>

          {mobilePickerOpen ? (
            <div className="project-switcher__mobile-menu" role="listbox" aria-labelledby="mobile-project-label">
              {projects.map((project) => {
                const isSelected = project._id === selectedProjectId;
                const approved = project.joinStatus === "approved";
                return (
                  <button
                    key={project._id}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    className={`project-switcher__mobile-option${isSelected ? " is-selected" : ""}`}
                    onClick={() => selectProject(project._id)}
                  >
                    <span className="project-switcher__mobile-option-copy">
                      <span className="project-switcher__mobile-option-name">{project.name}</span>
                      <span className="project-switcher__mobile-option-meta">
                        {approved ? <FaCheckCircle aria-hidden="true" /> : <FaClock aria-hidden="true" />}
                        {approved ? "Active" : "Awaiting approval"}
                      </span>
                    </span>
                    {isSelected ? <FaCheck className="project-switcher__mobile-option-check" aria-hidden="true" /> : null}
                  </button>
                );
              })}
            </div>
          ) : null}
        </div>

        <div className="project-switcher__list">
          {projects.map((project) => {
            const isSelected = project._id === selectedProjectId;
            const approved = project.joinStatus === "approved";
            return (
              <button
                key={project._id}
                type="button"
                className={`project-switcher__item${isSelected ? " is-selected" : ""}`}
                aria-pressed={isSelected}
                onClick={() => setSelectedProjectId(project._id)}
              >
                <span className="project-switcher__name">{project.name}</span>
                <span className="project-switcher__meta">
                  {approved ? <FaCheckCircle aria-hidden="true" /> : <FaClock aria-hidden="true" />}
                  {approved ? "Active" : "Awaiting approval"}
                </span>
              </button>
            );
          })}
        </div>
      </aside>

      <section className="expanded-view-container" aria-live="polite">
        {selectedProject?.joinStatus === "approved" ? (
          <ExpandedProjectView project={selectedProject} />
        ) : selectedProject ? (
          <div className="project-access-state">
            <FaClock size={20} aria-hidden="true" />
            <div>
              <h2>Membership awaiting approval</h2>
              <p>You can manage hours and reflections after the project leader approves your request.</p>
            </div>
          </div>
        ) : null}
      </section>
    </div>
  );
};

export default ConnectedProjectView;
